export interface SegmentData {
  stoppingPower: number
  views: number
  engagementRate: number
  engagementPoints: number
  probabilityToWin?: number
}

export interface VideoData {
  id: number
  name: string
  description: string
  videoUrl: string
  thumbnailUrl: string
  heatmapUrl: string
  techEnthusiast: SegmentData
  lifestyle: SegmentData
  // Fallbacks for components that haven't been fully updated yet
  stoppingPower: number
  views: number
  engagementRate: number
  engagementPoints: number
  category: 'ugc' | 'agency'
  type: string
  standardError: number
  sampleSize: number
}

const createPlaceholder = (text: string) =>
  `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="500"><rect width="100%" height="100%" fill="%23E5E5E5"/><text x="50%" y="50%" font-family="sans-serif" font-size="24" fill="%23737373" text-anchor="middle" dominant-baseline="middle">${encodeURIComponent(text)}</text></svg>`

interface HeatSpot {
  cx: number
  cy: number
  r: number
  opacity: number
}

// Builds a heatmap with an arbitrary set of hotspots so each asset can carry
// a visibly different attention pattern (position, spread and intensity).
const createHeatmap = (text: string, spots: HeatSpot[]) => {
  const blobs = spots
    .map(
      (s) =>
        `<circle cx="${s.cx}" cy="${s.cy}" r="${s.r}" fill="red" opacity="${s.opacity * 0.6}" filter="blur(24px)"/>` +
        `<circle cx="${s.cx}" cy="${s.cy}" r="${Math.round(s.r * 0.55)}" fill="orange" opacity="${s.opacity * 0.8}" filter="blur(14px)"/>` +
        `<circle cx="${s.cx}" cy="${s.cy}" r="${Math.round(s.r * 0.28)}" fill="yellow" opacity="${s.opacity}" filter="blur(8px)"/>`,
    )
    .join('')
  return `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="500"><rect width="100%" height="100%" fill="%23E5E5E5"/>${blobs}<text x="50%" y="94%" font-family="sans-serif" font-size="18" fill="%23737373" text-anchor="middle" dominant-baseline="middle">${encodeURIComponent(text)}</text></svg>`
}

export const mockVideoData: VideoData[] = [
  {
    id: 1,
    name: 'Asset A · Lifestyle 1',
    description:
      'Premium lifestyle visual, aspirational setting, minimal product presence',
    videoUrl: createPlaceholder('Asset A · Lifestyle 1'),
    thumbnailUrl:
      'https://cdn.magicpatterns.com/uploads/fHpzaTvXZH394EBUJWgt1T/Gemini_Generated_Image_gqhqxtgqhqxtgqhq.png',
    heatmapUrl:
      'https://cdn.magicpatterns.com/uploads/9kpey96fZK41yysygr8STc/A.png',
    techEnthusiast: {
      stoppingPower: 38,
      views: 7105,
      engagementRate: 0.25,
      engagementPoints: 15,
      probabilityToWin: 0.1,
    },
    lifestyle: {
      stoppingPower: 74,
      views: 8290,
      engagementRate: 0.45,
      engagementPoints: 35,
      probabilityToWin: 88.5,
    },
    stoppingPower: 38,
    views: 7105,
    engagementRate: 0.25,
    engagementPoints: 15,
    category: 'agency',
    type: 'image',
    standardError: 0.05,
    sampleSize: 30000,
  },
  {
    id: 2,
    name: 'Asset B · Lifestyle 2',
    description:
      'Lifestyle visual with an adventurous, journey-driven mood, product secondary.',
    videoUrl: createPlaceholder('Asset B · Lifestyle 2'),
    thumbnailUrl:
      'https://cdn.magicpatterns.com/uploads/sjNfiovwb9nFNSiaTaK1BP/Gemini_Generated_Image_6a6cg6a6cg6a6cg6.png',
    heatmapUrl:
      'https://cdn.magicpatterns.com/uploads/sKSevnvPRLNN1r5aUQDHoa/B.png',
    techEnthusiast: {
      stoppingPower: 41,
      views: 7260,
      engagementRate: 0.28,
      engagementPoints: 18,
      probabilityToWin: 0.2,
    },
    lifestyle: {
      stoppingPower: 66,
      views: 7980,
      engagementRate: 0.4,
      engagementPoints: 30,
      probabilityToWin: 11.2,
    },
    stoppingPower: 41,
    views: 7260,
    engagementRate: 0.28,
    engagementPoints: 18,
    category: 'agency',
    type: 'image',
    standardError: 0.04,
    sampleSize: 30000,
  },
  {
    id: 3,
    name: 'Asset C · Product 1',
    description:
      'Product-led visual, device front and center, spec-oriented message',
    videoUrl: createPlaceholder('Asset C · Product 1'),
    thumbnailUrl:
      'https://cdn.magicpatterns.com/uploads/teq5D5KBDeAxNXxPD4WE9c/Gemini_Generated_Image_q2g5hyq2g5hyq2g5.png',
    heatmapUrl:
      'https://cdn.magicpatterns.com/uploads/pq7hjYkk4GB6V323NENoma/C.png',
    techEnthusiast: {
      stoppingPower: 79,
      views: 8410,
      engagementRate: 0.5,
      engagementPoints: 45,
      probabilityToWin: 91.2,
    },
    lifestyle: {
      stoppingPower: 52,
      views: 7430,
      engagementRate: 0.32,
      engagementPoints: 22,
      probabilityToWin: 0.2,
    },
    stoppingPower: 79,
    views: 8410,
    engagementRate: 0.5,
    engagementPoints: 45,
    category: 'agency',
    type: 'image',
    standardError: 0.03,
    sampleSize: 30000,
  },
  {
    id: 4,
    name: 'Asset D · Product 2',
    description:
      'Product-led visual, performance and price-to-performance framing',
    videoUrl: createPlaceholder('Asset D · Product 2'),
    thumbnailUrl:
      'https://cdn.magicpatterns.com/uploads/nkkzsgpAb5NRgJaLgC7izi/Gemini_Generated_Image_4zongw4zongw4zon.png',
    heatmapUrl:
      'https://cdn.magicpatterns.com/uploads/3y8GvW8Gc3Rdqn1yd89bDX/D.png',
    techEnthusiast: {
      stoppingPower: 71,
      views: 8120,
      engagementRate: 0.45,
      engagementPoints: 38,
      probabilityToWin: 8.5,
    },
    lifestyle: {
      stoppingPower: 57,
      views: 7650,
      engagementRate: 0.35,
      engagementPoints: 25,
      probabilityToWin: 0.1,
    },
    stoppingPower: 71,
    views: 8120,
    engagementRate: 0.45,
    engagementPoints: 38,
    category: 'agency',
    type: 'image',
    standardError: 0.04,
    sampleSize: 30000,
  },
]

export const mockImageData = mockVideoData
export type ImageData = VideoData
